import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Gem, Globe, Building2, Percent } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { mockData } from '../data/mockData';
import { Button } from '../components/ui/button';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="label text-white text-sm font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: ${entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const MarketPage = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const market = mockData.market;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="market-page">
      {/* Header */}
      <div className="bg-primary text-white px-8 py-6">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/10"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Voltar' : 'Back'}
            </Button>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded hover:bg-white/20 transition-colors"
              data-testid="market-language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
          </div>
          <h1 className="font-serif text-3xl font-bold tracking-tight">
            {t('market')}
          </h1>
          <p className="text-white/70 mt-2 text-sm">
            {language === 'pt' 
              ? 'Informações de mercado, economia de Angola e indústria de diamantes' 
              : 'Market information, Angola economy and diamond industry'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-8 py-8">
        <div className="grid grid-cols-3 gap-6">
          {/* Diamond Price Index */}
          <div className="col-span-2 bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold-50 rounded-lg flex items-center justify-center">
                  <Gem className="w-5 h-5 text-gold-600" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-primary">
                    {t('diamondPriceIndex')}
                  </h3>
                  <p className="text-xs text-slate-500">USD per carat</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-serif font-semibold text-primary">${market.diamondPrices.current}</p>
                <div className={`flex items-center justify-end gap-1 ${market.diamondPrices.change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {market.diamondPrices.change > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span className="text-sm font-medium">{market.diamondPrices.change > 0 ? '+' : ''}{market.diamondPrices.change}%</span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={market.diamondPrices.history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" domain={['dataMin - 5', 'dataMax + 5']} />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="price" 
                  name="Price" 
                  stroke="#C5A059" 
                  strokeWidth={2} 
                  dot={{ fill: '#C5A059', r: 4 }}
                  activeDot={{ r: 6, fill: '#C5A059' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Angola Economy */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-primary">
                {t('angolaEconomy')}
              </h3>
            </div>
            <div className="space-y-5">
              <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                <span className="text-sm text-slate-600">{t('gdpGrowth')}</span>
                <span className="text-lg font-semibold text-emerald-600">+{market.angolaEconomy.gdpGrowth}%</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                <span className="text-sm text-slate-600">{t('inflation')}</span>
                <span className="text-lg font-semibold text-amber-600">{market.angolaEconomy.inflation}%</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                <span className="text-sm text-slate-600">{t('exchangeRate')}</span>
                <span className="text-lg font-semibold text-primary">{market.angolaEconomy.exchangeRate} AOA/USD</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                <span className="text-sm text-slate-600">{language === 'pt' ? 'Preço do Petróleo' : 'Oil Price'}</span>
                <span className="text-lg font-semibold text-primary">${market.angolaEconomy.oilPrice}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">{language === 'pt' ? 'Contribuição Mineira' : 'Mining Contribution'}</span>
                <span className="text-lg font-semibold text-gold-600">{market.angolaEconomy.miningContribution}%</span>
              </div>
            </div>
          </div>

          {/* Industry Overview */}
          <div className="col-span-3 bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-slate-600" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-primary">
                {t('industryOverview')}
              </h3>
            </div>
            <div className="grid grid-cols-4 gap-6">
              <div className="text-center p-6 bg-slate-50 rounded-lg">
                <p className="text-4xl font-serif font-semibold text-primary mb-2">{market.industryMetrics.globalProduction}</p>
                <p className="text-sm text-slate-500">{language === 'pt' ? 'M carats (produção global)' : 'M carats (global production)'}</p>
              </div>
              <div className="text-center p-6 bg-gold-50 rounded-lg">
                <p className="text-4xl font-serif font-semibold text-gold-600 mb-2">{market.industryMetrics.angolaShare}%</p>
                <p className="text-sm text-slate-500">{t('angolaShare')}</p>
              </div>
              <div className="text-center p-6 bg-emerald-50 rounded-lg">
                <p className="text-4xl font-serif font-semibold text-emerald-600 mb-2">+{market.industryMetrics.demandGrowth}%</p>
                <p className="text-sm text-slate-500">{language === 'pt' ? 'Crescimento da Procura' : 'Demand Growth'}</p>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <p className="text-4xl font-serif font-semibold text-blue-600 mb-2">{market.industryMetrics.labGrownShare}%</p>
                <p className="text-sm text-slate-500">{t('labGrownShare')}</p>
              </div>
            </div>

            {/* Market Commentary */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-primary mb-2">
                {language === 'pt' ? 'Comentário de Mercado' : 'Market Commentary'}
              </h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                {language === 'pt' 
                  ? 'O mercado global de diamantes naturais mantém-se resiliente, apesar da crescente quota de mercado dos sintéticos. Angola consolida a sua posição como um dos principais produtores mundiais, beneficiando de depósitos de alta qualidade e custos operacionais competitivos. A procura em mercados emergentes, particularmente na Ásia, continua a suportar os preços.'
                  : 'The global natural diamond market remains resilient despite the growing market share of synthetics. Angola consolidates its position as one of the world\'s leading producers, benefiting from high-quality deposits and competitive operating costs. Demand in emerging markets, particularly in Asia, continues to support prices.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketPage;
